import ko from "knockout";
import { filter } from "lodash";
import ViewModel from "viewModels/ViewModelTyped";
import SwitchedView from "viewModels/SwitchedView";

export type ViewData = {
    view: typeof ViewModel;
    arguments?: any[];
    template: string;
    persist: boolean;
    showHeader: boolean;
    viewTitle: string;
    onClose?: () => void;
};

type ViewMapEntry = ViewData & { viewKey: string };
const SIDE_PANEL_FOCUS_TIMEOUT = 100;

export default class SidePanelView extends ViewModel {
    private switchedView = this.subView(SwitchedView);
    private switchedViewsMap = ko
        .observable({})
        .extend({ deferred: true }) as ko.Observable<{
        [index: string]: ViewMapEntry;
    }>;

    public currentViewData = this.computed(() => {
        const viewKey = this.switchedView.getActiveView() as string;
        const activeView = this.switchedViewsMap()[viewKey];
        const isPersistDOM = activeView && activeView.persist;
        const view = isPersistDOM
            ? activeView.view
            : ko.unwrap(this.switchedView[viewKey]);

        if (!view) {
            return null;
        }

        return {
            view: view,
            viewTitle: activeView.viewTitle,
            showHeader:
                activeView.showHeader == null ? true : activeView.showHeader,
            viewKey: viewKey,
            onClose: activeView.onClose,
            template: activeView.template,
            persist: !!isPersistDOM,
        } as ViewMapEntry;
    });

    public persistedViews: ko.Computed<ViewMapEntry[]> = this.computed(() => {
        return filter(this.switchedViewsMap(), "persist");
    });

    close(): void {
        const { onClose } = this.currentViewData() || {};
        this.switchedView.setActiveView();
        if (onClose) {
            onClose();
        }

        // Return focus to the previously focused element when sidepanel closes
        setTimeout(() => {
            if (this.previouslyFocusedElement) {
                (this.previouslyFocusedElement as HTMLElement).focus();
            }
        }, SIDE_PANEL_FOCUS_TIMEOUT);
    }

    containsView(viewKey: string): boolean {
        return this.switchedViewsMap()[viewKey] !== undefined;
    }

    addView(viewKey: string, viewData: ViewData): void {
        if (this.containsView(viewKey)) {
            return;
        }

        const newSubViewsMap = this.switchedViewsMap();

        newSubViewsMap[viewKey] = { viewKey, ...viewData };

        const args = [
            viewKey,
            viewData.persist ? undefined : viewData.view,
            ...(viewData.arguments || []),
        ];

        if (viewData.persist) {
            newSubViewsMap[viewKey].view = this.subView(
                viewData.view,
                ...(viewData.arguments || [])
            );
        }

        if (viewData.persist) {
            this.switchedView.addPersistentView(...args);
        } else {
            this.switchedView.addView(...args);
        }

        this.switchedViewsMap(newSubViewsMap);
    }

    removeView(viewKey: string): void {
        if (viewKey === this.switchedView.getActiveView()) {
            this.close();
        }
        this.switchedView.disposeView(viewKey);
        const newSubViewsMap = this.switchedViewsMap();
        delete newSubViewsMap[viewKey];
        this.switchedViewsMap(newSubViewsMap);
    }

    removeViews(removeActiveView: boolean): void {
        for (const viewKey in this.switchedViewsMap()) {
            if (
                removeActiveView ||
                viewKey !== this.switchedView.getActiveView()
            )
                this.removeView(viewKey);
        }
    }

    // Track the element that had focus before opening sidepanel
    private previouslyFocusedElement: Element | null = null;

    setActiveView(viewKey: string, ...args: any[]): void {
        const currentViewData = this.currentViewData();
        if (!currentViewData || this.switchedView.getActiveView() !== viewKey) {
            // Store the currently focused element before opening sidepanel
            this.previouslyFocusedElement = document.activeElement;

            this.switchedView.setActiveView(viewKey, ...args);

            // Focus management: Move focus to the sidepanel when it opens
            setTimeout(() => {
                const sidepanel = document.getElementById("side-panel");
                if (sidepanel) {
                    sidepanel.focus();
                }
            }, SIDE_PANEL_FOCUS_TIMEOUT);
        }
    }

    isActiveView(viewKey: string): boolean {
        return this.switchedView.getActiveView() === viewKey;
    }
}
